/**
 * Typed service locator. Systems `provide` a service in `init`; other systems `get` it
 * (in their own init, if they run later, or lazily during updates).
 *
 * EXTENDING: declare your service type with declaration merging, next to the implementation:
 *
 *   declare module '../core/services' {
 *     interface ServiceMap {
 *       heat: HeatService;
 *     }
 *   }
 *
 * Then: `ctx.services.provide('heat', service)` and `ctx.services.get('heat')`.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ServiceMap {}

export class Services {
  private map = new Map<string, unknown>();

  provide<K extends keyof ServiceMap>(key: K, service: ServiceMap[K]): void {
    this.map.set(key as string, service);
  }

  /** Throws if the service has not been provided (catches init-order mistakes early). */
  get<K extends keyof ServiceMap>(key: K): ServiceMap[K] {
    const s = this.map.get(key as string);
    if (s === undefined) throw new Error(`Service '${String(key)}' not provided (check system order)`);
    return s as ServiceMap[K];
  }

  tryGet<K extends keyof ServiceMap>(key: K): ServiceMap[K] | undefined {
    return this.map.get(key as string) as ServiceMap[K] | undefined;
  }

  has(key: keyof ServiceMap): boolean {
    return this.map.has(key as string);
  }
}
